"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

export default function CustomImage({
    src,
    alt,
    caption,
    width = 800,
    height = 480,
    className = "",
    priority = false,
    fill = false,
    blurDataURL,
    style,
    ...props
}) {
    const [isOpen, setIsOpen] = useState(false);

    if (process.env.NODE_ENV === "development" && !alt) {
        // eslint-disable-next-line no-console
        console.warn("CustomImage requires alt text for accessibility.");
    }

    if (!src) {
        return (
            <div className="w-full my-6 text-center text-red-500 text-xs">
                (Image not found)
            </div>
        );
    }

    const isSvg = src.endsWith(".svg");
    const isExternal = /^https?:\/\//i.test(src);

    const openModal = useCallback(() => setIsOpen(true), []);
    const closeModal = useCallback(() => setIsOpen(false), []);

    // Close on ESC
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => e.key === "Escape" && closeModal();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, closeModal]);

    const Figure = ({ children }) => (
        <figure
            className={`my-6 flex flex-col items-center w-full md:max-w-[90%] mx-auto cursor-zoom-in ${className}`}
            onClick={openModal}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && openModal()
            }
            aria-label={`Open image: ${alt || "image"}`}
        >
            {children}
            {(caption || alt) && (
                <figcaption className="text-xs text-gray-500 text-center mt-2 select-none">
                    {caption || alt}
                </figcaption>
            )}
        </figure>
    );

    if (isSvg || isExternal) {
        return (
            <>
                <Figure>
                    <img
                        src={src}
                        alt={alt || ""}
                        width={width}
                        height={height}
                        style={{ maxWidth: "100%", height: "auto", ...style }}
                        loading={priority ? "eager" : "lazy"}
                        {...props}
                    />
                </Figure>

                {isOpen && (
                    <Modal onClose={closeModal}>
                        <img
                            src={src}
                            alt={alt || ""}
                            className="max-h-[90vh] max-w-[90vw] object-contain"
                            loading="eager"
                        />
                        {caption && (
                            <figcaption className="text-sm text-gray-300 mt-2 text-center select-none">
                                {caption}
                            </figcaption>
                        )}
                    </Modal>
                )}
            </>
        );
    }

    return (
        <>
            <Figure>
                <div
                    className={`relative w-full overflow-hidden rounded shadow ${
                        fill ? "h-[480px] md:h-[600px]" : ""
                    }`}
                    style={fill ? {} : { maxWidth: width }}
                >
                    <Image
                        src={src}
                        alt={alt || ""}
                        width={fill ? undefined : width}
                        height={fill ? undefined : height}
                        priority={priority}
                        fill={fill}
                        placeholder={blurDataURL ? "blur" : undefined}
                        blurDataURL={blurDataURL}
                        className={`rounded max-w-full ${
                            fill
                                ? "object-contain w-full h-full"
                                : "object-contain"
                        }`}
                        sizes="(max-width: 768px) 100vw, 800px"
                        loading={priority ? "eager" : "lazy"}
                        style={style}
                        {...props}
                    />
                </div>
            </Figure>

            {isOpen && (
                <Modal onClose={closeModal}>
                    <Image
                        src={src}
                        alt={alt || ""}
                        width={width}
                        height={height}
                        priority
                        className="max-h-[90vh] max-w-[90vw] object-contain"
                    />
                    {caption && (
                        <figcaption className="text-sm text-gray-300 mt-2 text-center select-none">
                            {caption}
                        </figcaption>
                    )}
                </Modal>
            )}
        </>
    );
}

function Modal({ children, onClose }) {
    const containerRef = useRef(null);

    // basic focus trap
    useEffect(() => {
        const prev = document.activeElement;
        containerRef.current?.focus();
        return () => prev?.focus?.();
    }, []);

    return (
        <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                ref={containerRef}
                tabIndex={-1}
                className="relative outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    aria-label="Close image preview"
                    onClick={onClose}
                    className="absolute top-2 right-2 text-white text-3xl font-bold hover:text-gray-300 focus:outline-none focus-visible:ring-2"
                >
                    &times;
                </button>
                {children}
            </div>
        </div>
    );
}
